import { useEffect, useState } from "react";
import { adminApi, extractApiError } from "../../lib/adminApi";
import {
    AdminPageHeader,
    SectionCard,
    FormActions,
} from "../../components/admin/FormPrimitives";
import { BilingualField } from "../../components/admin/BilingualField";
import { Plus, Trash2 } from "lucide-react";

export default function AdminContactInfo() {
    const [data, setData] = useState(null);
    const [state, setState] = useState({ status: "idle", error: "" });

    useEffect(() => {
        (async () => {
            try {
                const { data: doc } = await adminApi.get(
                    "/admin/contact-info",
                );
                setData(doc);
            } catch (err) {
                setState({ status: "error", error: extractApiError(err) });
            }
        })();
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        setState({ status: "submitting", error: "" });
        try {
            const payload = {
                firm_name: data.firm_name,
                address: data.address,
                phones: data.phones,
                email_main: data.email_main,
                email_cross_border: data.email_cross_border,
                whatsapp_local: data.whatsapp_local,
                whatsapp_canada: data.whatsapp_canada,
                opening_hours: data.opening_hours,
            };
            await adminApi.put("/admin/contact-info", payload);
            setState({ status: "success", error: "" });
            setTimeout(
                () =>
                    setState((s) =>
                        s.status === "success"
                            ? { status: "idle", error: "" }
                            : s,
                    ),
                2500,
            );
        } catch (err) {
            setState({ status: "error", error: extractApiError(err) });
        }
    };

    if (data === null) {
        return (
            <>
                <AdminPageHeader title="Coordonnées" />
                <SectionCard title="Chargement…">…</SectionCard>
            </>
        );
    }

    return (
        <div data-testid="admin-contact-info-page">
            <AdminPageHeader
                title="Coordonnées"
                subtitle="Ces informations pilotent le footer, la page contact et les liens WhatsApp."
            />
            <SectionCard title="Cabinet" testId="contact-info-card">
                <form onSubmit={onSubmit}>
                    <div className="mb-6">
                        <div className="eyebrow mb-2">Nom du cabinet</div>
                        <input
                            type="text"
                            className="field-box"
                            value={data.firm_name || ""}
                            onChange={(e) =>
                                setData({ ...data, firm_name: e.target.value })
                            }
                            data-testid="ci-firm-name"
                        />
                    </div>
                    <BilingualField
                        label="Adresse"
                        idPrefix="ci-address"
                        value={data.address}
                        onChange={(v) => setData({ ...data, address: v })}
                    />

                    <div className="mb-6">
                        <div className="eyebrow mb-2">Téléphones</div>
                        {(data.phones || []).map((p, i) => (
                            <div
                                key={i}
                                className="flex items-center gap-2 mb-2"
                            >
                                <input
                                    type="text"
                                    className="field-box"
                                    value={p}
                                    onChange={(e) => {
                                        const arr = [...(data.phones || [])];
                                        arr[i] = e.target.value;
                                        setData({ ...data, phones: arr });
                                    }}
                                    data-testid={`ci-phone-${i}`}
                                />
                                <button
                                    type="button"
                                    onClick={() => {
                                        const arr = (data.phones || []).filter(
                                            (_, k) => k !== i,
                                        );
                                        setData({ ...data, phones: arr });
                                    }}
                                    className="p-2 border border-ink-border hover:border-red-500 hover:text-red-400 text-neutral-400"
                                    data-testid={`ci-phone-remove-${i}`}
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={() =>
                                setData({
                                    ...data,
                                    phones: [...(data.phones || []), ""],
                                })
                            }
                            className="btn-outline-gold"
                            data-testid="ci-phone-add"
                        >
                            <Plus size={14} /> Ajouter un numéro
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div>
                            <div className="eyebrow mb-2">Courriel principal</div>
                            <input
                                type="email"
                                className="field-box"
                                value={data.email_main || ""}
                                onChange={(e) =>
                                    setData({
                                        ...data,
                                        email_main: e.target.value,
                                    })
                                }
                                data-testid="ci-email-main"
                            />
                        </div>
                        <div>
                            <div className="eyebrow mb-2">
                                Courriel dossiers transfrontaliers
                            </div>
                            <input
                                type="email"
                                className="field-box"
                                value={data.email_cross_border || ""}
                                onChange={(e) =>
                                    setData({
                                        ...data,
                                        email_cross_border: e.target.value,
                                    })
                                }
                                data-testid="ci-email-cross"
                            />
                        </div>
                        <div>
                            <div className="eyebrow mb-2">WhatsApp Burkina</div>
                            <input
                                type="text"
                                className="field-box"
                                value={data.whatsapp_local || ""}
                                onChange={(e) =>
                                    setData({
                                        ...data,
                                        whatsapp_local: e.target.value,
                                    })
                                }
                                data-testid="ci-wa-local"
                            />
                        </div>
                        <div>
                            <div className="eyebrow mb-2">WhatsApp Canada</div>
                            <input
                                type="text"
                                className="field-box"
                                value={data.whatsapp_canada || ""}
                                onChange={(e) =>
                                    setData({
                                        ...data,
                                        whatsapp_canada: e.target.value,
                                    })
                                }
                                data-testid="ci-wa-canada"
                            />
                        </div>
                    </div>

                    <div className="mb-6">
                        <div className="eyebrow mb-3">Horaires d'ouverture</div>
                        {(data.opening_hours || []).map((h, i) => (
                            <div
                                key={i}
                                className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3 border-l-2 border-gold/30 pl-4"
                            >
                                <BilingualField
                                    label={`Jour ${i + 1}`}
                                    idPrefix={`ci-oh-day-${i}`}
                                    value={h.day}
                                    onChange={(v) => {
                                        const arr = [...(data.opening_hours || [])];
                                        arr[i] = { ...arr[i], day: v };
                                        setData({ ...data, opening_hours: arr });
                                    }}
                                />
                                <BilingualField
                                    label="Horaires"
                                    idPrefix={`ci-oh-hours-${i}`}
                                    value={h.hours}
                                    onChange={(v) => {
                                        const arr = [...(data.opening_hours || [])];
                                        arr[i] = { ...arr[i], hours: v };
                                        setData({ ...data, opening_hours: arr });
                                    }}
                                />
                            </div>
                        ))}
                    </div>

                    <FormActions
                        saving={state.status === "submitting"}
                        status={state.status}
                        error={state.error}
                        testId="contact-info-form"
                    />
                </form>
            </SectionCard>
        </div>
    );
}
